import { type Entity, TrackingContext } from '@ps/web-core-analytics';
import classNames from 'clsx';
import { debounce } from 'lodash';
import type { ChangeEvent } from 'react';
import React, { useContext, useRef } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import { inputFilled, inputFocused } from '@swf/core/analytics';
import useTrackingLabel from '@swf/core/hooks/useTrackingLabel';
import {
  getIsAvailableOpportunity,
  getIsMultipleOpportunity
} from '@swf/core/models/sports/utils/betSlip/getIsOpportunity';
import { select } from '@swf/core/store';
import type { MultipleOpportunity, SingleOpportunity } from '@swf/generated/types/api/sports';

import useCreateEntities from '../../../../../../../../hooks/useCreateEntities';
import { useHasPromotionApplied } from '../../../../../../../../hooks/useHasPromotionApplied';
import { useNumberFromIntl } from '../../../../../../../../hooks/useNumberFromIntl';
import { intlStringToNumber } from '../../../../../../../../utils/intlStringToNumber';
import validateStake from '../../../../../../../../validation/validateStake';

import { TXTWCL_Stake } from './StakeInput.messages.json';
import styles from './StakeInput.scss';

interface Props {
  fieldName: string;
  numberOfLines: number;
  minStake: number;
  maxStake: number;
  minLineStake?: number;
  minStakeIncrement: number;
  maxPayout?: number;
  opportunity: SingleOpportunity | MultipleOpportunity;
  eachWaySelected?: boolean;
  /*
    This is passed here as a prop rather than calculated in validateStake as react final form needs to change the key
    prop in order to revalidate the stake on stake change.
    Using winPotentialReturn would mean the stake field loses focus on every stake change, with this solution it will
    only lose focus when the winPotentialReturn goes from below to above maxPayout or from above to below maxPayout.
  */
  isWinPotentialReturnAboveMaxPayout: boolean;
}

const StakeInput: React.FC<Props> = ({
  fieldName,
  numberOfLines,
  minStake,
  maxStake,
  minLineStake,
  minStakeIncrement,
  maxPayout,
  isWinPotentialReturnAboveMaxPayout,
  opportunity
}) => {
  const { createSingleEntities, createMultipleEntities } = useCreateEntities();
  const trackingFieldName = useTrackingLabel(TXTWCL_Stake);
  const trackingContext = useContext(TrackingContext);

  const isMobile = useSelector(select.device.isMobile);
  const isAndroid = useSelector(select.device.isAndroid);
  const site = useSelector(select.cms.getSiteId);
  const getNumberFromIntl = useNumberFromIntl();
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Scroll to the input for android as the keyboard opening hides the input on screen
   * Wait 500ms to allow the keyboard to load before scrolling
   */
  const scrollToInputOnAndroid = debounce(() => {
    if (isMobile && isAndroid) {
      inputRef.current?.scrollIntoView();
    }
  }, 500);

  const {
    input,
    meta: { error, submitting }
  } = useField(`${fieldName}.stakePerLine`, {
    validate: (num) =>
      validateStake({
        minStake,
        maxStake,
        minLineStake,
        minStakeIncrement,
        numberOfLines,
        site,
        maxPayout,
        isWinPotentialReturnAboveMaxPayout,
        stake: getNumberFromIntl(num)
      })
  });
  const { hasPromotionApplied } = useHasPromotionApplied(fieldName);

  const locale = useSelector(select.locale.get);
  const currency = useSelector(select.currency.get);
  const trackedError = useTrackingLabel(error?.translationTag, { 0: error?.value });
  const selectedPromotionPicker = useSelector(select.betSlip.getSelectedPromotionPicker);

  const handleEnterKeypress = (evt: React.KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    // Extract the separator from the stake (e.g. . or ,)
    const separator = new Intl.NumberFormat(locale, { style: 'currency', currency })
      .formatToParts(1.1)
      .find(({ type }) => type === 'decimal')?.value;

    // Ensure the stake is either an unsigned integer or an unsigned float with
    // up to 2 decimal places. Also ensures only one separater is used. This can
    // be a `.` in all locales plus the locale-specific decimal separater. We do
    // not support the use a thousand separators.
    const isValid = new RegExp(
      `^(?!(0\\d{2,}))\\d*[${separator !== '.' ? separator : ''}.]?\\d{0,2}$`,
      'i'
    ).test(value);

    if (isValid) {
      input.onChange(value);
    }
  };

  let entities: Entity[] = [];

  if (getIsMultipleOpportunity(opportunity) && getIsAvailableOpportunity(opportunity)) {
    entities = createMultipleEntities([opportunity]);
  } else if (getIsAvailableOpportunity(opportunity)) {
    entities = createSingleEntities([opportunity]);
  }

  return (
    <>
      <label htmlFor={input.name} className={styles.stakeLabel}>
        <Translation {...TXTWCL_Stake} />
      </label>
      <div
        className={classNames(styles.container, {
          [styles.error]: error,
          [styles.disabled]: submitting,
          [styles.promotionApplied]: hasPromotionApplied
        })}
      >
        <input
          {...input}
          maxLength={9}
          inputMode="decimal"
          disabled={Boolean(submitting || hasPromotionApplied || selectedPromotionPicker)}
          id={input.name}
          className={classNames(styles.input, {
            [styles.promotionAppliedInput]: hasPromotionApplied
          })}
          onChange={handleChange}
          onKeyPress={handleEnterKeypress}
          onBlur={(event: ChangeEvent<HTMLInputElement>) => {
            const { value } = event.target;

            inputFilled(
              {
                category: trackingContext.category,
                widget: trackingContext.widget,
                entities
              },
              {
                fieldName: trackingFieldName,
                numInput: intlStringToNumber(value),
                fieldType: 'number',
                fieldErrors: trackedError ? [trackedError] : null
              }
            );
          }}
          onClick={scrollToInputOnAndroid}
          onFocus={() => {
            inputFocused(
              {
                category: trackingContext.category,
                widget: trackingContext.widget,
                entities
              },
              {
                fieldName: trackingFieldName,
                fieldType: 'number'
              }
            );
          }}
          ref={inputRef}
        />
        {numberOfLines > 1 && (
          <span className={styles.numberOfLines} data-testid="number-of-lines">
            x{numberOfLines}
          </span>
        )}
      </div>
    </>
  );
};

export default (props: Props) => (
  <StakeInput
    {...props}
    // Whatever parameters are being used to calculate
    // the validation function need to be combined in such
    // a way that, when they change, this key prop
    // changes, thus reregistering this field (and its
    // validation function) with Final Form.
    key={[
      props.fieldName,
      props.minStake,
      props.maxStake,
      props.minStakeIncrement,
      props.isWinPotentialReturnAboveMaxPayout
    ].join('_')}
  />
);
